import { useEffect, useRef, useState } from "react";
import { TidyNotesButton } from "@/components/ai/TidyNotesButton";
import { Camera, Check, Loader2, LocateFixed, MapPin, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SearchSelect } from "@/components/ui/search-select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { StatusPill } from "@/components/patterns";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { LOCAL_PREFIX, isOffline, queueCheckIn, queueCheckOut } from "@/lib/visitQueue";
import { LOCATION_FLAG, announceVisitChange, getPosition, metres, uploadVisitPhoto, useVisitOutcomes, type Visit } from "@/lib/visits";

export interface VisitCustomer {
  type: "LEAD" | "ACCOUNT";
  id: string;
  label: string;
  /** A visit for a deal: it goes on the deal's timeline */
  dealId?: string;
  dealLabel?: string;
  /** The deal's scheduled meeting (task) the visit is for; checking out completes it */
  taskId?: string;
}

const leadLabel = (l: any) => [l.company_name, [l.first_name, l.last_name].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "Lead";

/** Lead or account picker, for visits started from the Visits page */
function CustomerPicker({ value, onChange }: { value: VisitCustomer | null; onChange: (c: VisitCustomer | null) => void }) {
  const [type, setType] = useState<"LEAD" | "ACCOUNT">(value?.type || "ACCOUNT");
  return (
    <div className="space-y-2">
      <div role="radiogroup" aria-label="Customer type" className="flex gap-1">
        {(["ACCOUNT", "LEAD"] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="radio"
            aria-checked={type === t}
            onClick={() => {
              setType(t);
              onChange(null);
            }}
            className={type === t ? "h-7 rounded-md bg-card px-2.5 text-xs font-semibold shadow-[0_0_0_1px_var(--border)]" : "h-7 rounded-md px-2.5 text-xs text-muted-foreground hover:bg-muted"}
          >
            {t === "ACCOUNT" ? "Account" : "Lead"}
          </button>
        ))}
      </div>
      <SearchSelect
        key={type}
        value={value?.id || ""}
        selectedLabel={value?.label}
        endpoint={type === "ACCOUNT" ? "/api/Account/List" : "/api/Lead/List"}
        placeholder={type === "ACCOUNT" ? "Search accounts" : "Search leads"}
        getLabel={(r) => (type === "ACCOUNT" ? r.name : leadLabel(r))}
        onChange={(id, row) => onChange(id ? { type, id, label: row ? (type === "ACCOUNT" ? row.name : leadLabel(row)) : "" } : null)}
      />
    </div>
  );
}

/** A camera button: opens the phone camera, shrinks the photo and attaches it to the visit */
export function VisitPhotoButton({ visitId, onDone, size = "sm" }: { visitId: string; onDone?: (v: Visit) => void; size?: "sm" | "default" }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { error } = useToast();
  const pick = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const v = await uploadVisitPhoto(visitId, file);
      setDone(true);
      onDone?.(v);
    } catch (e: any) {
      error("Could not add the photo", e.message || "Please try again");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <>
      <Button type="button" variant="outline" size={size} disabled={busy} onClick={() => input.current?.click()}>
        {busy ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : done ? <Check className="mr-1.5 size-4" /> : <Camera className="mr-1.5 size-4" />}
        {done ? "Photo added" : "Add photo"}
      </Button>
      <input ref={input} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
    </>
  );
}

/**
 * Check in at a customer: reads the phone's position, saves the visit and shows how far it was from the customer's saved
 * location. A far check-in is still saved (and flagged for the manager); a customer with no location can get this one.
 */
export function CheckInDialog({
  open,
  onOpenChange,
  customer,
  plannedVisit,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Fixed customer (from a lead or account page); leave out to pick one */
  customer?: VisitCustomer | null;
  /** A planned visit to start */
  plannedVisit?: Visit | null;
  onDone?: (v: Visit) => void;
}) {
  const { error, success } = useToast();
  const [picked, setPicked] = useState<VisitCustomer | null>(null);
  const [purpose, setPurpose] = useState("");
  const [step, setStep] = useState<"ready" | "locating" | "saving" | "done">("ready");
  const [problem, setProblem] = useState("");
  const [visit, setVisit] = useState<Visit | null>(null);
  const [locationSaved, setLocationSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(null);
    setPurpose(plannedVisit?.purpose || "");
    setStep("ready");
    setProblem("");
    setVisit(null);
    setLocationSaved(false);
  }, [open, plannedVisit?.id]);

  const target: VisitCustomer | null = plannedVisit ? { type: plannedVisit.related_type, id: plannedVisit.related_id, label: plannedVisit.related_label } : customer || picked;

  const checkIn = async () => {
    if (!target) return;
    setProblem("");
    setStep("locating");
    let body: Record<string, unknown> | null = null;
    try {
      const pos = await getPosition();
      setStep("saving");
      body = plannedVisit
        ? { visit_id: plannedVisit.id, related_type: plannedVisit.related_type, related_id: plannedVisit.related_id, lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy, purpose: purpose.trim() || undefined }
        : target.dealId
          ? { deal_id: target.dealId, task_id: target.taskId, lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy, purpose: purpose.trim() || undefined }
          : { related_type: target.type, related_id: target.id, lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy, purpose: purpose.trim() || undefined };
      const v = unwrapObject<Visit>(await api.post("/api/FieldVisit/CheckIn", body));
      setVisit(v);
      setStep("done");
      announceVisitChange();
      onDone?.(v);
    } catch (e: any) {
      // GPS works without signal: keep the check-in on the phone and send it when the connection is back
      if (body && isOffline(e)) {
        setVisit(queueCheckIn(body, target.label));
        setStep("done");
        return;
      }
      setProblem(e.message || "Could not check in");
      setStep("ready");
    }
  };

  const saveLocation = async () => {
    if (!visit || visit.check_in_lat == null || visit.check_in_lng == null) return;
    try {
      await api.post("/api/FieldVisit/CustomerLocation", { related_type: visit.related_type, related_id: visit.related_id, lat: visit.check_in_lat, lng: visit.check_in_lng });
      setLocationSaved(true);
      success("Location saved", `Next check-ins at ${visit.related_label} are measured from here.`);
    } catch (e: any) {
      error("Could not save the location", e.message || "Please try again");
    }
  };

  const flag = visit?.location_flag ? LOCATION_FLAG[visit.location_flag] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{step === "done" ? "You're checked in" : "Check in"}</DialogTitle>
          <DialogDescription>
            {step === "done" ? `At ${visit?.related_label}. Check out when you leave.` : "Your phone's location is saved with the visit. Allow location access when the browser asks."}
          </DialogDescription>
        </DialogHeader>

        {step === "done" && visit?.id.startsWith(LOCAL_PREFIX) ? (
          <p className="rounded-md bg-warning-soft px-3 py-2 text-[13px] text-warning">
            No signal here, so the check-in is saved on this phone with the time and place. It is sent by itself when you're back online. Photos can be added after that.
          </p>
        ) : step === "done" && visit ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {flag && <StatusPill tone={flag.tone}>{flag.label}</StatusPill>}
              <span className="text-[13px] text-muted-foreground">
                {[visit.check_in_distance_m != null ? `${metres(visit.check_in_distance_m)} from the saved location` : "", visit.check_in_accuracy_m != null ? `GPS ±${metres(visit.check_in_accuracy_m)}` : ""].filter(Boolean).join(" · ")}
              </span>
            </div>
            {visit.location_flag === "FAR" && (
              <p className="flex gap-2 rounded-md bg-danger-soft px-3 py-2 text-[13px] text-destructive">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                This check-in is far from where {visit.related_label} is saved, so your manager will see it flagged. If the customer has moved, ask them to update the location.
              </p>
            )}
            {visit.can_save_location && !locationSaved && (
              <div className="flex items-center justify-between gap-3 rounded-md bg-accent px-3 py-2 text-[13px] text-accent-foreground">
                <span>{visit.related_label} has no saved location. Save where you are now?</span>
                <Button size="sm" variant="outline" onClick={saveLocation}>
                  <LocateFixed className="mr-1.5 size-4" /> Save
                </Button>
              </div>
            )}
            <VisitPhotoButton visitId={visit.id} />
          </div>
        ) : (
          <div className="space-y-3">
            {target && (customer || plannedVisit) ? (
              <p className="flex items-center gap-2 text-[13px] font-medium">
                <MapPin className="size-4 text-muted-foreground" /> {target.label}
                {target.dealLabel && <span className="font-normal text-muted-foreground">· {target.dealLabel}</span>}
              </p>
            ) : (
              <CustomerPicker value={picked} onChange={setPicked} />
            )}
            <Input placeholder="Purpose (optional): demo, collection, new order…" value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={500} />
            {problem && (
              <p role="alert" className="flex gap-2 rounded-md bg-danger-soft px-3 py-2 text-[13px] text-destructive">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" /> {problem}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "done" ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button disabled={!target || step !== "ready"} onClick={checkIn}>
                {step === "ready" ? <MapPin className="mr-1.5 size-4" /> : <Loader2 className="mr-1.5 size-4 animate-spin" />}
                {step === "locating" ? "Finding you…" : step === "saving" ? "Checking in…" : "Check in here"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

/** Check out: how the visit went, notes, a follow-up date and a photo. The visit goes on the customer's timeline. */
export function CheckOutDialog({ visit, onOpenChange, onDone }: { visit: Visit | null; onOpenChange: (o: boolean) => void; onDone?: (v: Visit) => void }) {
  const { success, error } = useToast();
  const outcomes = useVisitOutcomes();
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!visit) return;
    setOutcome("");
    setNotes("");
    setFollowUp("");
  }, [visit?.id]);

  const save = async () => {
    if (!visit || !outcome) return;
    setBusy(true);
    let body: Record<string, unknown> | null = null;
    const keepOnPhone = () => {
      queueCheckOut(visit.id, body!, visit.related_label);
      success("Checked out on this phone", "No signal: it is sent with the time you left as soon as you're back online.");
      onOpenChange(false);
    };
    try {
      // Where they checked out is nice to have; a phone that can't tell quickly doesn't hold the rep up
      const pos = await getPosition(8000).catch(() => null);
      body = {
        outcome,
        notes: notes.trim() || undefined,
        next_follow_up_at: followUp ? new Date(followUp).toISOString() : undefined,
        lat: pos?.lat,
        lng: pos?.lng,
      };
      // A check-in still waiting on the phone: the check-out waits behind it
      if (visit.id.startsWith(LOCAL_PREFIX)) return keepOnPhone();
      const v = unwrapObject<Visit & { follow_up_task?: boolean }>(await api.post(`/api/FieldVisit/CheckOut/${visit.id}`, body));
      success("Checked out", `${v.duration_minutes ?? 0} min at ${v.related_label}${v.follow_up_task ? ". Follow-up task added." : "."}`);
      announceVisitChange();
      onDone?.(v);
      onOpenChange(false);
    } catch (e: any) {
      if (body && isOffline(e)) return keepOnPhone();
      error("Could not check out", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!visit} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Check out</DialogTitle>
          <DialogDescription>How did the visit to {visit?.related_label} go? It is added to the customer's log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <SearchableSelect value={outcome} onChange={(e) => setOutcome(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Choose the outcome…</option>
            {outcomes.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SearchableSelect>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was discussed, what they need" maxLength={5000} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none" />
          <TidyNotesButton text={notes} kind="VISIT" onTidied={(n, d) => { setNotes(n); if (d) setFollowUp(`${d}T10:00`); }} />
          <label className="block space-y-1 text-[13px]">
            <span className="text-muted-foreground">Next follow-up (makes a task)</span>
            <Input type="datetime-local" min={localInput(new Date())} value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
          </label>
          {visit && !visit.has_photo && !visit.id.startsWith(LOCAL_PREFIX) && <VisitPhotoButton visitId={visit.id} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !outcome} onClick={save}>
            {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
            Check out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Plan a visit for a day ahead; heads and admins can plan one for someone in their team */
export function PlanVisitDialog({ open, onOpenChange, customer, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; customer?: VisitCustomer | null; onDone?: (v: Visit) => void }) {
  const { success, error } = useToast();
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission("visit.manage");
  const [picked, setPicked] = useState<VisitCustomer | null>(null);
  const [when, setWhen] = useState("");
  const [purpose, setPurpose] = useState("");
  const [owner, setOwner] = useState("");
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = new Date(Date.now() + 86400000);
    t.setHours(11, 0, 0, 0);
    setWhen(localInput(t));
    setPicked(null);
    setPurpose("");
    setOwner(String(user?.Id || ""));
    if (canManage && !people.length)
      api
        .get("/api/User/Assignable?for=lead")
        .then((r) => setPeople(unwrapArray<any>(r).map((u) => ({ id: u.id, name: [u.first_name, u.last_name].filter(Boolean).join(" ") }))))
        .catch(() => undefined);
  }, [open]);

  const target = customer || picked;
  const save = async () => {
    if (!target || !when) return;
    setBusy(true);
    try {
      const v = unwrapObject<Visit>(
        await api.post("/api/FieldVisit/Plan", { ...(target.dealId ? { deal_id: target.dealId } : { related_type: target.type, related_id: target.id }), planned_at: new Date(when).toISOString(), purpose: purpose.trim() || undefined, owner_id: owner && owner !== user?.Id ? owner : undefined }),
      );
      success("Visit planned", `${v.related_label}, ${new Date(when).toLocaleString()}`);
      onDone?.(v);
      onOpenChange(false);
    } catch (e: any) {
      error("Could not plan the visit", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Plan a visit</DialogTitle>
          <DialogDescription>It shows on My Day that morning, ready to check in.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {customer ? (
            <p className="flex items-center gap-2 text-[13px] font-medium">
              <MapPin className="size-4 text-muted-foreground" /> {customer.label}
            </p>
          ) : (
            <CustomerPicker value={picked} onChange={setPicked} />
          )}
          <Input type="datetime-local" min={localInput(new Date())} value={when} onChange={(e) => setWhen(e.target.value)} />
          <Input placeholder="Purpose (optional)" value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={500} />
          {canManage && people.length > 1 && (
            <label className="block space-y-1 text-[13px]">
              <span className="text-muted-foreground">Who visits</span>
              <SearchableSelect value={owner} onChange={(e) => setOwner(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.id === user?.Id ? `${p.name} (you)` : p.name}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !target || !when} onClick={save}>
            {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
            Plan visit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
